import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import GlobalStyles from './styles/GlobalStyles';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollManager from './components/ScrollManager';
import Seo from './components/Seo';

// Eagerly loaded core page
import HomePage from './pages/HomePage';

// Lazy, preloadable routes (chunk prefetched on link hover/focus)
import {
  AppsPage, AppDetailPage, ContactPage, AboutPage, PrivacyPolicyPage, TermsOfUsePage,
  CraftedPage, StarQuestPage, ThrivePage, SterlingPage, BookhoodPage,
  ConduitFillPage, ConduitGuideIndex, ConduitGuideArticle,
  ChessClockGuideIndex, ChessClockGuideArticle, BowlingGuideIndex, BowlingGuideArticle,
  DinkGuideIndex, DinkGuideArticle, VolleyballGuideIndex, VolleyballGuideArticle,
  TracePage, DuctProPage, HaikuPage, BowlingPage, ChessClockPage,
  ChessClockPrivacy, ChessClockTerms, BowlingPrivacy, BowlingTerms,
  DinkPage, DinkPrivacy, DinkTerms,
  CigarHumidorPage, CigarPrivacy, CigarTerms,
  CraftPricingPage, CraftPricingPrivacy, CraftPricingTerms,
  CraftPricingGuideIndex, CraftPricingGuideArticle,
  PcosTrackerPage, PcosTrackerPrivacy, PcosTrackerTerms,
  PcosTrackerGuideIndex, PcosTrackerGuideArticle,
  TipCalculatorPage, TipCalculatorPrivacy, TipCalculatorTerms,
  TipCalculatorGuideIndex, TipCalculatorGuideArticle,
  SnookerScoreboardPage, SnookerScoreboardPrivacy, SnookerScoreboardTerms,
  SnookerScoreboardGuideIndex, SnookerScoreboardGuideArticle,
  DrumCoachPage, DrumCoachPrivacy, DrumCoachTerms, DrumCoachGuideIndex, DrumCoachGuideArticle,
  VolleyballScoreKeeperPage, VolleyballScoreKeeperPrivacy, VolleyballScoreKeeperTerms,
  DartsScorerPage, DartsScorerPrivacy, DartsScorerTerms,
  DartsScorerGuideIndex, DartsScorerGuideArticle,
  VolleyballPage, VolleyballPrivacy, VolleyballTerms,
  MochiRunPage, MochiRunPrivacy, MochiRunTerms, MochiRunGuideIndex, MochiRunGuideArticle,
  QuiltPlannerPage, LightMeterPage, BarbellCalculatorPage,
  TennisScoreKeeperPage, TennisScoreKeeperGuideIndex, TennisScoreKeeperGuideArticle,
  TennisScoreKeeperPrivacy, TennisScoreKeeperTerms,
  BoardFootPage, BoardFootGuideIndex, BoardFootGuideArticle,
  BoardFootPrivacy, BoardFootTerms,
  RoofPitchPage, RoofPitchGuideIndex, RoofPitchGuideArticle,
  RoofPitchPrivacy, RoofPitchTerms,
  SeatingChartPage, SeatingChartGuideIndex, SeatingChartGuideArticle,
  SeatingChartPrivacy, SeatingChartTerms,
  LeaseTrackerPage,
  NeedlepointPage, NeedlepointGuideIndex, NeedlepointGuideArticle,
  TraceGuideIndex,
  TraceGuideArticle,
  CigarHumidorGuideIndex,
  CigarHumidorGuideArticle,
  SterlingGuideIndex,
  SterlingGuideArticle,
  HaikuGuideIndex,
  HaikuGuideArticle,
  ThriveGuideIndex,
  ThriveGuideArticle,
  StarQuestGuideIndex,
  StarQuestGuideArticle,
  DuctulatorGuideIndex,
  DuctulatorGuideArticle, NotFound,
} from './lazyRoutes';

// Seamless fallback: brand cream, no flash of a different color or layout shift.
const LoadingComponent = () => (
  <div
    aria-hidden="true"
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--cream)',
    }}
  />
);

const App = () => {
  return (
    <Router>
        <GlobalStyles />
        <ScrollManager />
        <Seo />
        <a className="skip-link" href="#main">Skip to content</a>
        <Navbar />
        <main id="main" tabIndex={-1}>
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/apps" element={<AppsPage />} />
              <Route path="/app/:id" element={<AppDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy/:app" element={<PrivacyPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms/:app" element={<TermsOfUsePage />} />
              <Route path="/terms" element={<TermsOfUsePage />} />
              <Route path="/crafted" element={<CraftedPage />} />
              <Route path="/starquest" element={<StarQuestPage />} />
              <Route path="/thrive" element={<ThrivePage />} />
              <Route path="/sterling" element={<SterlingPage />} />
              <Route path="/bookhood" element={<BookhoodPage />} />
              <Route path="/conduit-fill" element={<ConduitFillPage />} />
              <Route path="/conduit-fill/guides" element={<ConduitGuideIndex />} />
              <Route path="/conduit-fill/guides/:slug" element={<ConduitGuideArticle />} />
              <Route path="/haiku" element={<HaikuPage />} />
              <Route path="/bowling" element={<BowlingPage />} />
              <Route path="/bowling/guides" element={<BowlingGuideIndex />} />
              <Route path="/bowling/guides/:slug" element={<BowlingGuideArticle />} />
              <Route path="/bowling/privacy" element={<BowlingPrivacy />} />
              <Route path="/bowling/terms" element={<BowlingTerms />} />
              <Route path="/dink" element={<DinkPage />} />
              <Route path="/dink/guides" element={<DinkGuideIndex />} />
              <Route path="/dink/guides/:slug" element={<DinkGuideArticle />} />
              <Route path="/dink/privacy" element={<DinkPrivacy />} />
              <Route path="/dink/terms" element={<DinkTerms />} />
              <Route path="/cigar-humidor" element={<CigarHumidorPage />} />
              <Route path="/cigar-humidor/privacy" element={<CigarPrivacy />} />
              <Route path="/cigar-humidor/terms" element={<CigarTerms />} />
              <Route path="/craft-pricing" element={<CraftPricingPage />} />
              <Route path="/craft-pricing/guides" element={<CraftPricingGuideIndex />} />
              <Route path="/craft-pricing/guides/:slug" element={<CraftPricingGuideArticle />} />
              <Route path="/craft-pricing/privacy" element={<CraftPricingPrivacy />} />
              <Route path="/craft-pricing/terms" element={<CraftPricingTerms />} />
              <Route path="/pcos-tracker" element={<PcosTrackerPage />} />
              <Route path="/pcos-tracker/guides" element={<PcosTrackerGuideIndex />} />
              <Route path="/pcos-tracker/guides/:slug" element={<PcosTrackerGuideArticle />} />
              <Route path="/pcos-tracker/privacy" element={<PcosTrackerPrivacy />} />
              <Route path="/pcos-tracker/terms" element={<PcosTrackerTerms />} />
              <Route path="/tip-calculator" element={<TipCalculatorPage />} />
              <Route path="/tip-calculator/guides" element={<TipCalculatorGuideIndex />} />
              <Route path="/tip-calculator/guides/:slug" element={<TipCalculatorGuideArticle />} />
              <Route path="/tip-calculator/privacy" element={<TipCalculatorPrivacy />} />
              <Route path="/tip-calculator/terms" element={<TipCalculatorTerms />} />
              <Route path="/snooker-scoreboard" element={<SnookerScoreboardPage />} />
              <Route path="/snooker-scoreboard/guides" element={<SnookerScoreboardGuideIndex />} />
              <Route path="/snooker-scoreboard/guides/:slug" element={<SnookerScoreboardGuideArticle />} />
              <Route path="/snooker-scoreboard/privacy" element={<SnookerScoreboardPrivacy />} />
              <Route path="/snooker-scoreboard/terms" element={<SnookerScoreboardTerms />} />
              <Route path="/drum-coach" element={<DrumCoachPage />} />
              <Route path="/drum-coach/guides" element={<DrumCoachGuideIndex />} />
              <Route path="/drum-coach/guides/:slug" element={<DrumCoachGuideArticle />} />
              <Route path="/drum-coach/privacy" element={<DrumCoachPrivacy />} />
              <Route path="/drum-coach/terms" element={<DrumCoachTerms />} />
              <Route path="/volleyball-score-keeper" element={<VolleyballScoreKeeperPage />} />
              <Route path="/volleyball-score-keeper/privacy" element={<VolleyballScoreKeeperPrivacy />} />
              <Route path="/volleyball-score-keeper/terms" element={<VolleyballScoreKeeperTerms />} />
              <Route path="/darts-scorer" element={<DartsScorerPage />} />
              <Route path="/darts-scorer/guides" element={<DartsScorerGuideIndex />} />
              <Route path="/darts-scorer/guides/:slug" element={<DartsScorerGuideArticle />} />
              <Route path="/darts-scorer/privacy" element={<DartsScorerPrivacy />} />
              <Route path="/darts-scorer/terms" element={<DartsScorerTerms />} />
              <Route path="/volleyball" element={<VolleyballPage />} />
              <Route path="/volleyball/guides" element={<VolleyballGuideIndex />} />
              <Route path="/volleyball/guides/:slug" element={<VolleyballGuideArticle />} />
              <Route path="/volleyball/privacy" element={<VolleyballPrivacy />} />
              <Route path="/volleyball/terms" element={<VolleyballTerms />} />
              <Route path="/mochirun" element={<MochiRunPage />} />
              <Route path="/mochirun/guides" element={<MochiRunGuideIndex />} />
              <Route path="/mochirun/guides/:slug" element={<MochiRunGuideArticle />} />
              <Route path="/mochirun/privacy" element={<MochiRunPrivacy />} />
              <Route path="/mochirun/terms" element={<MochiRunTerms />} />
              <Route path="/quilt-planner" element={<QuiltPlannerPage />} />
              <Route path="/lightmeter" element={<LightMeterPage />} />
              <Route path="/barbell-calculator" element={<BarbellCalculatorPage />} />
              <Route path="/tennis-score-keeper" element={<TennisScoreKeeperPage />} />
              <Route path="/tennis-score-keeper/guides" element={<TennisScoreKeeperGuideIndex />} />
              <Route path="/tennis-score-keeper/guides/:slug" element={<TennisScoreKeeperGuideArticle />} />
              <Route path="/tennis-score-keeper/privacy" element={<TennisScoreKeeperPrivacy />} />
              <Route path="/tennis-score-keeper/terms" element={<TennisScoreKeeperTerms />} />
              <Route path="/boardfoot" element={<BoardFootPage />} />
              <Route path="/boardfoot/guides" element={<BoardFootGuideIndex />} />
              <Route path="/boardfoot/guides/:slug" element={<BoardFootGuideArticle />} />
              <Route path="/boardfoot/privacy" element={<BoardFootPrivacy />} />
              <Route path="/boardfoot/terms" element={<BoardFootTerms />} />
              <Route path="/roof-pitch" element={<RoofPitchPage />} />
              <Route path="/roof-pitch/guides" element={<RoofPitchGuideIndex />} />
              <Route path="/roof-pitch/guides/:slug" element={<RoofPitchGuideArticle />} />
              <Route path="/roof-pitch/privacy" element={<RoofPitchPrivacy />} />
              <Route path="/roof-pitch/terms" element={<RoofPitchTerms />} />
              <Route path="/seating-chart" element={<SeatingChartPage />} />
              <Route path="/seating-chart/guides" element={<SeatingChartGuideIndex />} />
              <Route path="/seating-chart/guides/:slug" element={<SeatingChartGuideArticle />} />
              <Route path="/seating-chart/privacy" element={<SeatingChartPrivacy />} />
              <Route path="/seating-chart/terms" element={<SeatingChartTerms />} />
              <Route path="/lease-tracker" element={<LeaseTrackerPage />} />
              <Route path="/lease-tracker/support" element={<LeaseTrackerPage />} />
              <Route path="/needlepoint" element={<NeedlepointPage />} />
              <Route path="/needlepoint/guides" element={<NeedlepointGuideIndex />} />
              <Route path="/needlepoint/guides/:slug" element={<NeedlepointGuideArticle />} />
              <Route path="/trace/guides" element={<TraceGuideIndex />} />
              <Route path="/trace/guides/:slug" element={<TraceGuideArticle />} />
              <Route path="/cigar-humidor/guides" element={<CigarHumidorGuideIndex />} />
              <Route path="/cigar-humidor/guides/:slug" element={<CigarHumidorGuideArticle />} />
              <Route path="/sterling/guides" element={<SterlingGuideIndex />} />
              <Route path="/sterling/guides/:slug" element={<SterlingGuideArticle />} />
              <Route path="/haiku/guides" element={<HaikuGuideIndex />} />
              <Route path="/haiku/guides/:slug" element={<HaikuGuideArticle />} />
              <Route path="/thrive/guides" element={<ThriveGuideIndex />} />
              <Route path="/thrive/guides/:slug" element={<ThriveGuideArticle />} />
              <Route path="/starquest/guides" element={<StarQuestGuideIndex />} />
              <Route path="/starquest/guides/:slug" element={<StarQuestGuideArticle />} />
              <Route path="/ductulator/guides" element={<DuctulatorGuideIndex />} />
              <Route path="/ductulator/guides/:slug" element={<DuctulatorGuideArticle />} />
              <Route path="/pocket-chess-clock" element={<ChessClockPage />} />
              <Route path="/pocket-chess-clock/guides" element={<ChessClockGuideIndex />} />
              <Route path="/pocket-chess-clock/guides/:slug" element={<ChessClockGuideArticle />} />
              <Route path="/pocket-chess-clock/privacy" element={<ChessClockPrivacy />} />
              <Route path="/pocket-chess-clock/terms" element={<ChessClockTerms />} />
              <Route path="/trace" element={<TracePage />} />
              <Route path="/duct-pro" element={<DuctProPage />} />
              <Route path="/ductulator" element={<DuctProPage />} />
              {/* Homepage redesign previews - internal comparison only. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <div id="route-announcer" className="visually-hidden" aria-live="polite" aria-atomic="true" />
    </Router>
  );
};

export default App;
